import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { racesAPI } from '@/services/racesApi';
import { raceTypesAPI } from '@/services/raceTypesApi';
import { registrationsAPI } from '@/services/registrationsApi';
import { configAPI } from '@/services/configApi';
import enLocale from '../../../locales/en.json';
import mkLocale from '../../../locales/mk.json';
import alLocale from '../../../locales/al.json';
import PublicHomePageView from './publichomepage.view';

const localeMap: Record<string, any> = { en: enLocale, mk: mkLocale, al: alLocale };

const PublicHomePage = () => {
  const [lang, setLang] = useState('en');
  const [races, setRaces] = useState<any[]>([]);
  const [selectedRace, setSelectedRace] = useState<any>(null);
  const [raceTypes, setRaceTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    country: '',
    gender: '',
    dateOfBirth: '',
    raceTypeId: '',
    participantType: 'Individual',
    teamName: '',
    tshirtSize: 'M',
    discountCode: '',
    emergencyContactPhone: '',
    cookiesConsent: false,
    privacyPolicy: false,
  });

  const navigate = useNavigate();
  const t = localeMap[lang];

  useEffect(() => {
    loadRaces();
  }, []);

  useEffect(() => {
    if (selectedRace) {
      loadRaceTypes(selectedRace.id);
    }
  }, [selectedRace]);

  const loadRaces = async () => {
    try {
      const config = await configAPI.getPublicConfig();
      const params: Record<string, any> = {};
      if (config.organizerId) params.organizerId = config.organizerId;
      params.status = 'Upcoming';
      const racesData = await racesAPI.getRaces(params);
      setRaces(racesData);
      if (racesData && racesData.length > 0) {
        const sorted = [...racesData].sort((a: any, b: any) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
        setSelectedRace(sorted[0]);
      }
    } catch (err) {
      console.error('Failed to load races:', err);
    }
  };

  const loadRaceTypes = async (raceId: number) => {
    try {
      const types = await raceTypesAPI.getRaceTypes(raceId);
      setRaceTypes(types);
      if (types && types.length > 0) {
        setFormValues(prev => ({ ...prev, raceTypeId: types[0].id }));
      }
    } catch (err) {
      console.error('Failed to load race types:', err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormValues(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formValues.privacyPolicy) {
      setError(t?.reg?.privacyPolicyRequired || 'You must accept the privacy policy');
      return;
    }

    if (!selectedRace || !formValues.raceTypeId) {
      setError('Please select a race and race type');
      return;
    }

    setLoading(true);

    try {
      const registrationData = {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        email: formValues.email,
        phone: formValues.phone,
        city: formValues.city,
        country: formValues.country,
        gender: formValues.gender,
        dateOfBirth: new Date(formValues.dateOfBirth).toISOString(),
        participantType: formValues.participantType,
        teamName: formValues.teamName || null,
        tshirtSize: formValues.tshirtSize,
        discountCode: formValues.discountCode || null,
        emergencyContactPhone: formValues.emergencyContactPhone || null,
        cookiesConsent: formValues.cookiesConsent,
        privacyPolicy: formValues.privacyPolicy,
        raceId: selectedRace.id,
        raceTypeId: parseInt(formValues.raceTypeId),
      };

      const result = await registrationsAPI.createRegistration(registrationData);

      if (result && result.id) {
        navigate(`/registration-success?id=${result.id}`);
      }
    } catch (err: any) {
      console.error('Registration failed:', err);
      setError(err.message || 'Registration failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <PublicHomePageView
      lang={lang}
      setLang={setLang}
      t={t}
      races={races}
      selectedRace={selectedRace}
      raceTypes={raceTypes}
      formValues={formValues}
      loading={loading}
      error={error}
      handleInputChange={handleInputChange}
      handleCheckboxChange={handleCheckboxChange}
      handleSubmit={handleSubmit}
    />
  );
};

export default PublicHomePage;
